// ============================================================================
// STUDIO TV SERVICE
// Frontend API client for the fortnightly Co-Creation Studio roundup films
// ============================================================================

import { api } from './api';

export interface StudioTvItem {
  id: string;
  title: string;
  description: string;
  edition: string;       // YYYY-MM-DD
  durationSec: number;
  posterUrl: string | null;
  pinned: boolean;
  views: number;
  publishedAt: string;
  playlistId: string;
  position: number;      // 0 unless the playlist is manual
  // Publishers only. Empty: everyone. name is null for a deleted role.
  audience?: StudioTvAudienceRole[];
}

export interface StudioTvAudienceRole {
  id: string;
  name: string | null;
}

export interface StudioTvPlaylist {
  id: string;
  title: string;
  description: string;
  order: 'newest' | 'manual';
  isDefault: boolean;
  // Publishers only. Empty: everyone.
  audience?: StudioTvAudienceRole[];
  videoCount?: number;
}

export interface StudioTvList {
  items: StudioTvItem[];
  playlists: StudioTvPlaylist[];
}

export interface StudioTvPlaylistInput {
  title?: string;
  description?: string;
  order?: 'newest' | 'manual';
  audienceRoleIds?: string[];
}

export interface StudioTvItemWithVideo extends StudioTvItem {
  videoUrl: string;
  videoUrlExpiresAt: string;
}

// Items sorted pinned first, then newest; playlists default first, then by creation
export async function listStudioTv(): Promise<StudioTvList> {
  const res = await api.get<StudioTvList>('/api/studio-tv');
  return { items: res.data.items, playlists: res.data.playlists ?? [] };
}

export async function createStudioTvPlaylist(input: StudioTvPlaylistInput & { title: string }): Promise<StudioTvPlaylist> {
  const res = await api.post<{ playlist: StudioTvPlaylist }>('/api/studio-tv/playlists', input);
  return res.data.playlist;
}

export async function updateStudioTvPlaylist(id: string, input: StudioTvPlaylistInput): Promise<StudioTvPlaylist> {
  const res = await api.patch<{ playlist: StudioTvPlaylist }>(`/api/studio-tv/playlists/${id}`, input);
  return res.data.playlist;
}

export async function deleteStudioTvPlaylist(id: string): Promise<void> {
  await api.delete(`/api/studio-tv/playlists/${id}`);
}

// videoIds: exactly the playlist's films, in the new order
export async function reorderStudioTvPlaylist(id: string, videoIds: string[]): Promise<void> {
  await api.put(`/api/studio-tv/playlists/${id}/order`, { videoIds });
}

export async function moveStudioTvItem(id: string, playlistId: string): Promise<StudioTvItem> {
  const res = await api.patch<{ item: StudioTvItem }>(`/api/studio-tv/${id}`, { playlistId });
  return res.data.item;
}

export async function getStudioTvItem(id: string): Promise<StudioTvItemWithVideo> {
  const res = await api.get<{ item: StudioTvItemWithVideo }>(`/api/studio-tv/${id}`);
  return res.data.item;
}

export async function recordStudioTvView(id: string): Promise<number> {
  const res = await api.post<{ views: number }>(`/api/studio-tv/${id}/view`);
  return res.data.views;
}

export async function setStudioTvPinned(id: string, pinned: boolean): Promise<StudioTvItem> {
  const res = await api.patch<{ item: StudioTvItem }>(`/api/studio-tv/${id}`, { pinned });
  return res.data.item;
}

export async function setStudioTvAudience(id: string, audienceRoleIds: string[]): Promise<StudioTvItem> {
  const res = await api.patch<{ item: StudioTvItem }>(`/api/studio-tv/${id}`, { audienceRoleIds });
  return res.data.item;
}

export async function listStudioTvRoles(): Promise<Array<{ id: string; name: string }>> {
  const res = await api.get<{ roles: Array<{ id: string; name: string }> }>('/api/studio-tv/roles');
  return res.data.roles;
}

export function audienceLabel(audience: StudioTvAudienceRole[] | undefined): string | null {
  if (!audience?.length) return null;
  return audience.map(r => r.name ?? 'Deleted role').join(', ');
}

export interface StudioSessionStats {
  delivered: number;
  deliveredLast30Days: number;
  customersServed: number;
  upcoming: number;
}

// Approved studio sessions as counts only; open to every Studio TV viewer
export async function getStudioSessionStats(): Promise<StudioSessionStats> {
  const res = await api.get<{ stats: StudioSessionStats }>('/api/studio-tv/sessions');
  return res.data.stats;
}

export async function deleteStudioTvItem(id: string): Promise<void> {
  await api.delete(`/api/studio-tv/${id}`);
}

// The backend also refuses anyone outside Version 1 (roundups name clients).
export function isVersion1Email(email: string | undefined | null): boolean {
  return typeof email === 'string' && email.toLowerCase().endsWith('@version1.com');
}

export const LAST_SEEN_KEY = 'studioTvLastSeen';
export const LAST_SEEN_EVENT = 'studio-tv-seen';

export function newestPublishedAt(items: StudioTvItem[]): string | null {
  return items.reduce<string | null>((max, i) => (!max || i.publishedAt > max ? i.publishedAt : max), null);
}

export function markStudioTvSeen(items: StudioTvItem[]) {
  const newest = newestPublishedAt(items);
  if (!newest) return;
  try {
    const prev = localStorage.getItem(LAST_SEEN_KEY);
    if (!prev || newest > prev) localStorage.setItem(LAST_SEEN_KEY, newest);
  } catch {}
  window.dispatchEvent(new Event(LAST_SEEN_EVENT));
}

export function hasUnseenStudioTv(items: StudioTvItem[]): boolean {
  const newest = newestPublishedAt(items);
  if (!newest) return false;
  try {
    const seen = localStorage.getItem(LAST_SEEN_KEY);
    return !seen || newest > seen;
  } catch {
    return false;
  }
}
